import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal, Button } from '@shared-ui/components';
import { adminApi } from '../../api/adminApi';
import './ConfigHistoryDrawer.css';

function shortTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
}

/** Version history for one pipeline config — resolved snapshots, view + restore. */
export function ConfigHistoryDrawer({ configName, onClose, onRestored }: {
  configName: string;
  onClose: () => void;
  onRestored: () => void;
}) {
  const qc = useQueryClient();
  const [selectedSeq, setSelectedSeq] = useState<number | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['pipelineConfigRevisions', configName],
    queryFn: () => adminApi.getPipelineConfigRevisions(configName),
  });
  const revisions = data?.revisions ?? [];

  const { data: detail } = useQuery({
    queryKey: ['pipelineConfigRevision', configName, selectedSeq],
    queryFn: () => adminApi.getPipelineConfigRevision(configName, selectedSeq!),
    enabled: selectedSeq != null,
  });

  const restore = useMutation({
    mutationFn: (seq: number) => adminApi.restorePipelineConfigRevision(configName, seq),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pipelineConfigRevisions', configName] });
      onRestored();
    },
  });

  return (
    <Modal isOpen onClose={onClose} title={`History — ${configName}`} size="xl">
      <div className="cfg-hist">
        {isLoading ? <p>Loading…</p> : revisions.length === 0 ? (
          <p className="cfg-hist-empty">No version history yet. A snapshot is recorded the next time this config changes.</p>
        ) : (
          <table className="dt cfg-hist-table">
            <thead><tr><th>#</th><th>Action</th><th>When</th><th>By</th><th>Operations</th><th>Fingerprint</th><th></th></tr></thead>
            <tbody>
              {revisions.map(r => (
                <tr key={r.seq} className={selectedSeq === r.seq ? 'cfg-hist-active' : ''}>
                  <td>{r.seq}</td>
                  <td><span className={`cfg-hist-action cfg-hist-${r.action}`}>{r.action}{r.parentSeq ? ` (#${r.parentSeq})` : ''}</span></td>
                  <td className="cfg-hist-when">{shortTime(r.changedAt)}</td>
                  <td>{r.changedBy ?? '—'}</td>
                  <td>{r.operations.length ? r.operations.join(', ') : '—'}</td>
                  <td><code className="cfg-hist-fp" title={r.fingerprint}>{r.fingerprint.slice(0, 10) || '—'}</code></td>
                  <td className="cfg-hist-actions">
                    <Button variant="secondary" size="sm" onClick={() => setSelectedSeq(s => s === r.seq ? null : r.seq)}>
                      {selectedSeq === r.seq ? 'Hide' : 'View'}
                    </Button>
                    <Button variant="outline" size="sm" disabled={restore.isPending || r.action === 'delete'}
                      onClick={() => { if (confirm(`Restore config "${configName}" to version #${r.seq}? This overwrites the current config.`)) restore.mutate(r.seq); }}>
                      Restore
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {restore.error && <p className="cfg-hist-err">{(restore.error as Error).message}</p>}

        {selectedSeq != null && (
          <div className="cfg-hist-detail">
            <h4>Version #{selectedSeq} — resolved snapshot</h4>
            {!detail ? <p>Loading…</p> : (
              <>
                {Object.entries(detail.resolved).map(([op, snap]) => (
                  <div key={op} className="cfg-hist-op">
                    <strong>{op}</strong>
                    <span className="cfg-hist-muted"> — {Object.keys(snap.prompts ?? {}).length} prompt(s), models: {snap.modelIds?.join(', ') || '—'}</span>
                  </div>
                ))}
                <details className="cfg-hist-raw">
                  <summary>Raw resolved snapshot (JSON)</summary>
                  <pre>{JSON.stringify(detail.resolved, null, 2)}</pre>
                </details>
              </>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
